import { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { formatDateString, showStudentURL } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";

import { fetchLevels } from "../../student/studentSlice";
import { fetchAssignments } from "../assignmentListSlice";
import { sendUnfinishedNoticeAPI } from "../../notification/notificationAPI";


import Pagination from "../../../components/Pagination";
import LinkCopyButton from "../../../components/complex/LinkCopyButton";
import WorksheetTypeBox from "./common/WorksheetTypeBox";
import CustomCheckbox from "../../../components/CustomCheckbox";
import WorksheetSearchBar from "./common/WorksheetSearchBar";
import WorksheetSelectBar from "./common/WorksheetSelectBar";
import DeleteWorksheetModal from "./common/DeleteWorksheetModal";
import FilteringTab from "./common/FilteringTab/FilteringTab";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

const UnfinishedWorksheetTable = () => {
  const [isMobileW, setIsMobileW] = useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  useEffect(() => {
    const f = () => setIsMobileW(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);

  const dispatch = useDispatch();

  const { levels, allStudents } = useSelector((state) => state.student);
  const { results, current_page, total_pages } = useSelector(
    (state) => state.assignments
  );

  const [selectedParams, setSelectedParams] = useState({
    levelId: null,
    studentId: null,
  });
  const [selectedAssignments, setSelectedAssignments] = useState([]);

  // 고른 미완수자에게 미완료 안내 알림톡을 보낸다. 자동 배치가 아니라 이 버튼만
  // 발송하므로, 누른 그 자리에서 실제로 나간다.
  const handleSendUnfinishedNotice = (assignmentList) =>
    sendUnfinishedNoticeAPI(assignmentList.map((item) => item.id))
      .then((response) => {
        const { sent_count: sentCount, failed_list: failedList } = response.data;
        const lines = [`${sentCount}명에게 보냈습니다.`];
        if (failedList.length) {
          lines.push("", "보내지 못한 학생", ...failedList);
        }
        alert(lines.join("\n"));
        setSelectedAssignments([]);
      })
      .catch((error) => {
        alert(error?.response?.data?.error || "발송에 실패했습니다.");
      });
  const [keyword, setKeyword] = useState("");
  const [typeSelect, setTypeSelect] = useState("all");

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  useEffect(() => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(
      fetchAssignments({
        page: 1,
        type: typeParam,
        isFinished: false,
        keyword: keyword,
        levelId: selectedParams.levelId,
        studentId: selectedParams.studentId,
        studentGrade: selectedParams.studentGrade,
      })
    );
    dispatch(fetchLevels());
    setSelectedAssignments([]);
  }, [dispatch, keyword, typeSelect, selectedParams]);

  const handleCheckboxChange = (checked, assignment) => {
    if (checked) {
      // 체크 O
      setSelectedAssignments((prev) => [...prev, assignment]);
    } else {
      // 체크 해제
      setSelectedAssignments((prev) =>
        prev.filter((prevAssignment) => prevAssignment.id !== assignment.id)
      );
    }
  };

  //   (2) 헤더(전체선택) 체크박스 핸들러
  const handleHeaderCheckboxChange = (checked) => {
    if (checked) {
      setSelectedAssignments(results);
    } else {
      // 해제
      setSelectedAssignments([]);
    }
  };

  // (3) 헤더 체크박스가 "완전히 체크" 상태인지 판별 (현재 페이지 모두 체크된 경우)
  const isHeaderChecked =
    results.length > 0 &&
    results.every((currentAssignment) =>
      selectedAssignments.some(
        (selectedAssignment) => selectedAssignment.id === currentAssignment.id
      )
    );

  const handlePageClick = (page) => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(
      fetchAssignments({
        page,
        type: typeParam,
        isFinished: false,
        keyword: keyword,
        levelId: selectedParams.levelId,
        studentId: selectedParams.studentId,
        studentGrade: selectedParams.studentGrade,
      })
    );
  };

  return (
    <div
      style={{
        padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "10px 14px 32px" : "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobileW ? "center" : "flex-end",
          gap: "8px",
          marginBottom: "18px",
          flexWrap: "nowrap",
        }}
      >
        {!isMobileW && (
          <h3 className="section-title">
            과제 미완수자 명단
          </h3>
        )}
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
        />
        <div style={{ flex: isMobileW ? 1 : "none", width: isMobileW ? "auto" : "30%", minWidth: isMobileW ? 0 : "220px" }}>
          <WorksheetSearchBar handleFilter={setKeyword} />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          // [모바일 수정] 필터를 표 위로 쌓기 (표 압착 → 글자 세로 붕괴 방지)
          flexDirection: Math.min(window.innerWidth, window.screen.width) <= 768 ? "column" : "row",
          alignItems: "stretch",
          marginTop: "10px",
        }}
      >
        <FilteringTab
          levels={levels}
          allStudentList={allStudents}
          selectedParams={selectedParams}
          setSelectedParams={setSelectedParams}
        />
        <div
          style={{
            width: isMobileW ? "100%" : "85%",
            minWidth: 0,
            backgroundColor: "#FFF",
            paddingBottom: "5px",
            borderRadius: "18px",
            border: "1px solid #E6EDEC",
            boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
            overflowX: isMobileW ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table
            className="data-table"
            style={{
              minWidth: Math.min(window.innerWidth, window.screen.width) <= 768 ? "720px" : undefined, // [모바일 수정]
            }}
          >
            <colgroup>
              <col style={{ width: "48px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "56px" }} />
              <col />
              <col style={{ width: "88px" }} />
              <col style={{ width: "88px" }} />
            </colgroup>
            <tbody>
              <tr className="data-table__header-row">
                <td className="cell-center">
                  <CustomCheckbox
                    onChange={(e) =>
                      handleHeaderCheckboxChange(e.target.checked)
                    }
                    checked={isHeaderChecked}
                  />
                </td>
                <td className="cell-nowrap">이름</td>
                <td className="cell-center cell-nowrap">링크</td>
                <td className="cell-nowrap">학습지명</td>
                <td className="cell-center cell-nowrap">출제일</td>
                <td className="cell-center cell-nowrap">마감일</td>
              </tr>
              {results &&
                results.map((item, index) => {
                  const isChecked = selectedAssignments.includes(item);
                  return (
                    <tr key={item.id}>
                      <td
                        style={{
                          textAlign: "center",
                        }}
                      >
                        <CustomCheckbox
                          checked={isChecked}
                          onChange={(e) =>
                            handleCheckboxChange(e.target.checked, item)
                          }
                        />
                      </td>
                      <td className="cell-nowrap">
                        <a
                          href={showStudentURL(item.student.session)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="학생 페이지 열기"
                          className="cell-link"
                        >
                          {item.student.name}
                        </a>
                      </td>
                      <td className="cell-center">
                        <LinkCopyButton
                          URL={showStudentURL(item.student.session)}
                        />
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {item.is_retry && (
                          <span style={{
                            backgroundColor: "#E8F7EE",
                            color: "#1DB05B",
                            padding: "4px 10px",
                            borderRadius: "999px",
                            fontSize: "12px",
                            fontWeight: 800,
                            whiteSpace: "nowrap",
                            flexShrink: 0
                          }}>
                            복습 {item.retry_info?.retry_count || ''}회차
                          </span>
                        )}
                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{item.title}</span>
                        <span style={{ flexShrink: 0 }}><WorksheetTypeBox type={item.type} /></span>
                      </td>
                      <td className="cell-center">
                        {formatDateString(item.created_at)}
                      </td>
                      <td className="cell-center">
                        {item.closed_at ? formatDateString(item.closed_at) : "-"}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* 페이지네이션 */}
          <Pagination
            visiblePages={visiblePages}
            current_page={current_page}
            handlePageClick={handlePageClick}
          />
        </div>
        {selectedAssignments.length > 0 && (
          <DeleteWorksheetModal
            worksheets={selectedAssignments}
            setSelectedWorksheets={setSelectedAssignments}
            type={"assignment"}
            onSendNotice={handleSendUnfinishedNotice}
          />
        )}
      </div>
    </div>
  );
};

export default UnfinishedWorksheetTable;
